import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { useContext, useCallback } from 'react';

import classnames from 'classnames';

import { testId } from 'ui-components/services/testid-service';
import { Text, TextType } from 'ui-components/components/text';

import { emptyObject } from 'ui-components/default-props/empty';
import { SellerSettingsOptions } from '../../../../enums/seller-settings/seller-settings-options';
import { useExemptionOrITN, useDefaultExemptionCode, useITNSetting } from '../../../../hooks/use-seller-setting';
import { CustomsContext } from '../../context/context';
import type { CustomsContextValue } from '../../types';
import type { Currency } from '../../../../prop-types/currency-types';
import { CustomsField } from '../customs-field';
import { CustomsTextInput } from '../customs-text-input';

import commonStyle from '../common-style.module.less';
import style from './style.module.less';
import messages from '../messages';

const FOREIGN_TRADE_URL = 'https://www.census.gov/foreign-trade/aes/aesdirect/transitiontoace.html';
const ITN_LINE_ITEM_TOTAL_THRESHOLD = 2500;

type FulfillmentPlan = {
  items?: Array<{
    totalPrice: Currency;
  }>;
};

export const areLineItemsTotalOverLimit = (fulfillmentPlan: FulfillmentPlan) => {
  const { items } = fulfillmentPlan;
  if (items) {
    const total = items.reduce((t, i) => t + i.totalPrice.value, 0);
    return total > ITN_LINE_ITEM_TOTAL_THRESHOLD;
  }
  return false;
};

const shouldHideBasedOnIntlSettings = (
  exemptionOrITN: string | undefined,
  itnSetting: string | undefined,
  defaultExemptionCode: string | undefined,
  isUsps: boolean,
  fulfillmentPlan: FulfillmentPlan,
) => {
  return (
    !exemptionOrITN ||
    (exemptionOrITN === SellerSettingsOptions.itn.value && !itnSetting) ||
    (exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value && !defaultExemptionCode) ||
    (exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value && !isUsps) ||
    (exemptionOrITN === SellerSettingsOptions.itn.value &&
      itnSetting === SellerSettingsOptions.lineItems.value &&
      !areLineItemsTotalOverLimit(fulfillmentPlan))
  );
};

interface ItnOrExemptionWarningProps {
  isExemption: boolean;
}

const ItnOrExemptionWarning: React.FC<ItnOrExemptionWarningProps> = ({ isExemption }) => {
  const messageDesciptor = isExemption ? messages.missingExemptionWarning : messages.missingItnWarning;
  return (
    <div className={classnames(commonStyle['full-input'], style['itn-warning'])}>
      <div className={style['icon-container']}>
        <FontAwesomeIcon className={style.icon} icon={faExclamationTriangle} />
      </div>
      <span>
        <Text
          formattedMessageValues={{
            a: (chunks) => (
              <a href={FOREIGN_TRADE_URL} rel='noreferrer' target='_blank'>
                {chunks}
              </a>
            ),
          }}
          textType={TextType.Caption}
          value={messageDesciptor}
        />
      </span>
    </div>
  );
};

interface ItnOrExemptionProps {
  fulfillmentPlan?: FulfillmentPlan;
  isUsps?: boolean;
}

export const ItnOrExemption: React.FC<ItnOrExemptionProps> = ({ fulfillmentPlan = emptyObject, isUsps = false }) => {
  const { exemptionCodeOrITN, updateItn }: CustomsContextValue = useContext(CustomsContext);
  const exemptionOrITN = useExemptionOrITN();
  const defaultExemptionCode = useDefaultExemptionCode();
  const itnSetting = useITNSetting();

  const handleChange = useCallback(
    (value: string | number) => {
      updateItn(value);
    },
    [updateItn],
  );

  if (shouldHideBasedOnIntlSettings(exemptionOrITN, itnSetting, defaultExemptionCode, isUsps, fulfillmentPlan)) {
    return null;
  }

  const messageDescriptor =
    exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value ? messages.uspsExemptionLabel : messages.itnLabel;

  return (
    <CustomsField label={messageDescriptor}>
      <CustomsTextInput
        {...testId('customs-widget-itn-input')}
        className={commonStyle['standard-input']}
        onChange={handleChange}
        type='text'
        value={exemptionCodeOrITN}
      />
      {!exemptionCodeOrITN && <ItnOrExemptionWarning isExemption={exemptionOrITN === SellerSettingsOptions.uspsExemptionCode.value} />}
    </CustomsField>
  );
};
